import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CustomFields, CustomFieldPolicyPermission } from '../../custom-fields.interface';
import { TemplateCustomFieldsService } from '../services/template-custom-fields.service';
import { TemplateGroupModalComponent } from '../template-group-modal/template-group-modal.component';
import { catchError, filter, switchMap, take, takeUntil } from 'rxjs/operators';
import { Subject, NEVER } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import _cloneDeep from 'lodash-es/cloneDeep';
import { TemplateGroupUndeletableModalComponent } from '../template-group-undeletable-modal/template-group-undeletable-modal.component';
import { CustomFieldModalComponent } from '../../custom-field-modal/custom-field-modal.component';
import { isCustomItemGroup, trackCustomFieldByFn } from '../../custom-fields.constants';
import { SmartModalService } from '@certemy/ui-components';
import { PolicyPermissionsService } from 'services/policy-permission/policy-permissions.service';

@Component({
  selector: 'cm-template-custom-container',
  templateUrl: './template-custom-container.component.html',
  styleUrls: ['./template-custom-container.component.scss'],
})
export class TemplateCustomContainerComponent implements OnInit, OnDestroy {
  policyPermissions: CustomFieldPolicyPermission = {
    addFieldGroup: false,
    addField: false,
    editField: false,
    editGroup: false,
    deleteField: false,
    deleteFieldGroup: false,
  };

  set fields(value: CustomFields.Item[]) {
    this._fields = value;
    this._fieldsCopy = _cloneDeep(value);
  }

  get fields(): CustomFields.Item[] {
    return this._fields;
  }

  trackCustomFieldByFn = trackCustomFieldByFn;

  private _fields: CustomFields.Item[] = [];
  private _fieldsCopy: CustomFields.Item[] = [];
  @ViewChild(TemplateGroupModalComponent) groupModal: TemplateGroupModalComponent;
  @ViewChild(TemplateGroupUndeletableModalComponent)
  groupUndeletableModal: TemplateGroupUndeletableModalComponent;
  @ViewChild(CustomFieldModalComponent) customFieldModal: CustomFieldModalComponent;

  private readonly onDestroy$ = new Subject<void>();

  constructor(
    private templateCustomFieldsService: TemplateCustomFieldsService,
    private activatedRoute: ActivatedRoute,
    private smartModalService: SmartModalService,
    private policyPermissionsService: PolicyPermissionsService,
  ) {}

  ngOnInit() {
    this.fields = this.activatedRoute.snapshot.data.data;

    this.policyPermissionsService
      .hasPermissions('settings/organizations/custom_fields', [
        'add_field',
        'add_field_group',
        'edit_field',
        'delete_field',
        'edit_group',
        'delete_field_group',
      ])
      .pipe(take(1))
      .subscribe(({ add_field, add_field_group, edit_field, delete_field, edit_group, delete_field_group }) => {
        this.policyPermissions = {
          addField: add_field,
          addFieldGroup: add_field_group,
          editField: edit_field,
          editGroup: edit_group,
          deleteField: delete_field,
          deleteFieldGroup: delete_field_group,
        };
      });
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  onDrop({ value }: { value: CustomFields.Item }, group_id: number) {
    const item_id = (isCustomItemGroup(value) ? value.fields[0] : value).field_id;
    const prev_item_id = this.getPrevFieldId(this.fields, item_id);
    const schema = { who: { with_group: isCustomItemGroup(value), item_id }, where: { prev_item_id, group_id } };
    this.templateCustomFieldsService
      .reorderCustom(schema)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (res) => (this.fields = res),
        error: () => this.rollbackFields(),
    });
  }

  onAddField(group_id = null) {
    this.openUpdateFieldModal({ group_id });
  }

  onEditField(field: CustomFields.Field) {
    this.openUpdateFieldModal(field);
  }

  onAddGroup(group?: CustomFields.Group) {
    this.groupModal
      .open(group)
      .pipe(
        take(1),
        filter((haveGroup) => !!haveGroup),
        switchMap((newGroup) =>
          this.templateCustomFieldsService.createOrUpdateCustomGroup(newGroup).pipe(catchError(() => NEVER)),
        ),
        takeUntil(this.onDestroy$),
      )
      .subscribe((res) => (this.fields = res));
  }

  onDeleteField(field: CustomFields.Field) {
    if (field.use_in_profile) {
      this.smartModalService
        .openConfirm({
          title: 'Delete field',
          body: "Please note that the field will be removed from User's profile",
          successButtonText: 'Confirm',
        })
        .pipe(take(1))
        .subscribe((confirmDelete) => {
          if (confirmDelete) {
            this.deleteField(field);
          }
        });
    } else {
      this.deleteField(field);
    }
  }

  deleteField(field: CustomFields.Field) {
    this.templateCustomFieldsService
      .deleteCustomField(field)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((res) => (this.fields = res));
  }

  onDeleteGroup(group: CustomFields.Group) {
    const undeletableFields = group.fields.filter((field) => field.is_mapped);

    if (undeletableFields.length) {
      this.groupUndeletableModal
        .open({
          groupName: group.name,
          fieldNames: undeletableFields.map((field) => field.label),
        })
        .pipe(take(1), takeUntil(this.onDestroy$))
        .subscribe();
      return;
    }

    const hasAvailableInProfileField = group.fields.some((field) => field.use_in_profile);

    if (hasAvailableInProfileField) {
      this.smartModalService
        .openConfirm({
          title: 'Delete field',
          body: "Please note that the group contains field(s) that will be removed from User's profile",
          successButtonText: 'Confirm',
        })
        .pipe(take(1))
        .subscribe((confirmDelete) => {
          if (confirmDelete) {
            this.deleteGroup(group);
          }
        });
    } else {
      this.deleteGroup(group);
    }
  }

  deleteGroup(group: CustomFields.Group) {
    this.templateCustomFieldsService
      .deleteCustGroup(group.group_id)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((res) => (this.fields = res));
  }

  getCustomGroups = (items: CustomFields.Item[]): CustomFields.Group[] => {
    return items.filter((el) => isCustomItemGroup(el));
  };

  private openUpdateFieldModal(custField) {
    const custGroups = this.getCustomGroups(this.fields);
    this.customFieldModal
      .open({ custGroups, custField })
      .pipe(
        take(1),
        filter((haveItems) => !!haveItems),
        takeUntil(this.onDestroy$),
      )
      .subscribe((res) => (this.fields = res));
  }

  private getPrevFieldId(fields: CustomFields.Item[], item_id: number): number {
    const flattenFields = this.flatFields(fields);
    const itemIndex = flattenFields.findIndex((el) => el.field_id === item_id);
    const prevItem = flattenFields[itemIndex - 1];
    return prevItem ? prevItem.field_id : null;
  }

  private flatFields(fields: CustomFields.Item[]): CustomFields.Field[] {
    return fields.reduce((acc, cur) => [...acc, ...(isCustomItemGroup(cur) ? cur.fields : [cur])], []);
  }

  private rollbackFields() {
    this._fields = _cloneDeep(this._fieldsCopy);
  }
}
